import { useCreateHabitAllowed, useDuplicateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyDuplicateObject16, Trash16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenDeleteTasksDialog } from 'shared/contexts/multi-task-handlers';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';

import { MenuGroups } from './types';

export type LifeCycleListProps = {
  tasks: Task[];
  onClose: () => void;
  registerItem?: (
    group: MenuGroups,
    index: number,
    element: HTMLButtonElement | null,
  ) => void;
  registerGroup?: MenuGroups;
  hasBottomBorder?: boolean;
};

export const LifeCycleList: React.FC<LifeCycleListProps> = ({
  tasks,
  onClose,
  registerItem,
  registerGroup,
  hasBottomBorder,
}) => {
  const { t } = useTranslation();
  const duplicateTasks = useDuplicateTasks();
  const isCreateHabitAllowed = useCreateHabitAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const openDeleteTasksDialog = useOpenDeleteTasksDialog();

  const hasHabit = useMemo(
    () =>
      tasks.some((task) =>
        [TaskType.Habit, TaskType.Repeating].includes(task.type),
      ),
    [tasks],
  );

  const isDuplicateAllowed = useMemo(
    () => isCreateHabitAllowed.allowed || !hasHabit,
    [isCreateHabitAllowed.allowed, hasHabit],
  );

  const onDuplicate = () => {
    if (!isDuplicateAllowed) {
      openPremiumDialog();
      onClose();
      return;
    }

    duplicateTasks(tasks);
    onClose();
  };

  const onDelete = () => {
    openDeleteTasksDialog(tasks);
    onClose();
  };

  return (
    <>
      <PopupMenuList hasBottomBorder={hasBottomBorder}>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDuplicate}
            start={<Icon icon={CopyDuplicateObject16} />}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 0, el)}
          >
            {t('context-menus.task.labels.duplicate')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDelete}
            start={<Icon icon={Trash16} />}
            startColor={Colors.Error}
            textColor={Colors.Error}
            ref={(el) => registerGroup && registerItem?.(registerGroup, 1, el)}
          >
            {t('context-menus.task.labels.delete')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </>
  );
};
